import { useCallback, useEffect, useState, useRef } from 'react';
import { Paper, Tabs, Tab } from '@mui/material';
import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router';
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import b64toBlob from 'b64-to-blob';

import { useAnalyticContext } from '../../../hooks/analyticContext';
import { configureStackedBarChartForCost, configureBhrtcChart } from '../../ChartConfiguration/ChartConfiguration';
import { getAcCostGraph, getFfCostGraph, getBhRfCostGraph, getBfCostGraph, getPhCostGraph, exportAnalyticMembers, getLastClaimUpdatedOn } from './AnalyticService';
import ClaimsPerformance from '../../../Models/Claims/ClaimsPreview.model';
import FilterOptions from './FilterOptions';
import { ClaimAnalyticEnum } from '../../../Enum/ClaimAnalyticEnum';
import { formatDate } from '../../../utils/dateHelpers';

const MemberCostComparison = () => {
    const history = useNavigate();
    const ref = useRef(null);
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [isGraphShow, setIsGraphShow] = useState<boolean>(false);
    const [isPrepared, setIsPrepared] = useState<boolean>(false);
    const [graphSummary, setGraphSummary] = useState<ClaimsPerformance[]>([]);
    const [bhRtcGraphSummary, setBhRtcGraphSummary] = useState<any>([]);
    const [chartOptions, setChartOptions] = useState({});
    const [downLoading, setDownLoading] = useState<boolean>(false);
    const [lastUpdatedOn, setLastUpdatedOn] = useState('');
    const chartHeight = 450;

    const {
        costSummaryHealthPlanIds,
        setCostSummaryHealthPlanIds,
        costSummaryTeam,
        setCostSummaryTeam,
        showCostSummaryInActiveTeams,
        setShowCostSummaryInActiveTeams,
        costSummaryTab,
        setCostSummaryTab,
        costSummaryYears,
        setCostSummaryYears,
        costSummaryMonths,
        setCostSummaryMonths,
    } = useAnalyticContext();

    const bhRtcName = 'BH RTC';
    const bhRfName = 'BH RF';

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        if (!isGraphShow) {
            setChartOptions(
                configureStackedBarChartForCost(
                    'AC Cost',
                    '',
                    '',
                    'Cost',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [''],
                    'AcCost',
                    history,
                    chartHeight
                )
            );
        }

        const data = await getLastClaimUpdatedOn();
        setLastUpdatedOn(data ? formatDate(new Date(`${data.split('T')[0]}T00:00:00`), 'MM/dd/yyyy') : '');
    }, [isFetched, setIsFetched, isGraphShow, history]);

    useEffect(() => {
        if (!isFetched && costSummaryHealthPlanIds && costSummaryHealthPlanIds.length > 0) {
            changeFilter();
            setIsFetched(true);
        }
        initialPage();
        // eslint-disable-next-line react-hooks/exhaustive-deps;
    }, [costSummaryHealthPlanIds]);

    const changeFilter = async (summaryTab?: number) => {
        summaryTab = summaryTab || costSummaryTab;
        if (summaryTab === 1) {
            // Acute BH Costs
            setIsGraphShow(false);
            setChartOptions(
                configureStackedBarChartForCost(
                    'Acute BH Cost',
                    '',
                    '',
                    'Cost',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    'FfCost',
                    history,
                    chartHeight
                )
            );
            const data = await getFfCostGraph(
                costSummaryHealthPlanIds,
                costSummaryTeam,
                costSummaryYears,
                costSummaryMonths,
                showCostSummaryInActiveTeams
            );
            setGraphSummary(data);
            setIsPrepared(true);
            setIsGraphShow(true);
        }
        else if (summaryTab === 2) {
            // Acute Care Costs
            setIsGraphShow(false);
            setChartOptions(
                configureStackedBarChartForCost(
                    'AC Cost',
                    '',
                    '',
                    'Cost',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    'AcCost',
                    history,
                    chartHeight
                )
            );
            const data = await getAcCostGraph(
                costSummaryHealthPlanIds,
                costSummaryTeam,
                costSummaryYears,
                costSummaryMonths,
                showCostSummaryInActiveTeams
            );
            setGraphSummary(data);
            setIsPrepared(true);
            setIsGraphShow(true);
        }
        else if (summaryTab === 3) {
            // BH RTC/RF Costs
            setIsGraphShow(false);
            setChartOptions(
                configureBhrtcChart(
                    'BH RTC/RF Costs',
                    '',
                    '',
                    'Cost',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    'BhRtcCost',
                    history,
                    chartHeight
                )
            );
            let response = [];
            let bhrtc, bhrf;
            const bfrfCost = await getBhRfCostGraph(
                costSummaryHealthPlanIds,
                costSummaryTeam,
                costSummaryYears,
                costSummaryMonths,
                showCostSummaryInActiveTeams
            );
            if (Boolean(bfrfCost)) {
                bhrf = bfrfCost;
                bhrf.Name = bhRfName;
                response.push(bhrf);
            }
            const bhrtcCost = await getBfCostGraph(
                costSummaryHealthPlanIds,
                costSummaryTeam,
                costSummaryYears,
                costSummaryMonths,
                showCostSummaryInActiveTeams
            );
            if (Boolean(bhrtcCost)) {
                bhrtc = bhrtcCost;
                bhrtc.Name = bhRtcName;
                response.push(bhrtc);
            }
            setBhRtcGraphSummary(response);
            setIsPrepared(true);
            setIsGraphShow(true);
        }
        else if (summaryTab === 4) {
            // Pharmaceutical Costs
            setIsGraphShow(false);
            setChartOptions(
                configureStackedBarChartForCost(
                    'Pharmaceutical Cost',
                    '',
                    '',
                    'Cost',
                    [{ name: '', data: [0], showInLegend: false, title: { text: null } }],
                    [0],
                    'PhCost',
                    history,
                    chartHeight
                )
            );
            const data = await getPhCostGraph(
                costSummaryHealthPlanIds,
                costSummaryTeam,
                costSummaryYears,
                costSummaryMonths,
                showCostSummaryInActiveTeams
            );
            setGraphSummary(data);
            setIsPrepared(true);
            setIsGraphShow(true);
        };
    }

    useEffect(() => {
        if (costSummaryTab === 1 && graphSummary && isPrepared) {
            let xAxisValues = graphSummary.map(x => x.Category);

            let yAxisValues = [
                {
                    name: 'Acute BH Cost',
                    data: graphSummary.map((x) => ({
                        y: x.TotalCost,
                        total: x.MemberCount
                    })),
                    color: '#6495ED',
                    showInLegend: false
                }
            ];

            setChartOptions(
                configureStackedBarChartForCost(
                    'Acute BH Cost',
                    '',
                    '',
                    'Cost',
                    yAxisValues,
                    xAxisValues,
                    'FfCost',
                    history,
                    chartHeight
                )
            );

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        else if (costSummaryTab === 2 && graphSummary && isPrepared) {
            let xAxisValues = graphSummary.map(x => x.Category);

            let yAxisValues = [
                {
                    name: 'AC Cost',
                    data: graphSummary.map((x) => ({
                        y: x.TotalCost,
                        total: x.MemberCount
                    })),
                    color: '#6495ED',
                    showInLegend: false
                }
            ];

            setChartOptions(
                configureStackedBarChartForCost(
                    'AC Cost',
                    '',
                    '',
                    'Cost',
                    yAxisValues,
                    xAxisValues,
                    'AcCost',
                    history,
                    chartHeight
                )
            );

            setIsGraphShow(true);
            setIsPrepared(false);
        }
        else if (costSummaryTab === 3 && bhRtcGraphSummary && isPrepared) {
            let xAxisValues = [];
            let yAxisValues = [];
            let isData = false;

            bhRtcGraphSummary.forEach((bhrtcData) => {
                let yValues = [];
                bhrtcData.forEach((bhrtc) => {

                    if (bhrtc.TotalCost && bhrtc.TotalCost > 0) {
                        isData = true;
                    }

                    yValues.push({
                        y: bhrtc.TotalCost,
                        name: bhrtc.Category,
                        title: bhrtc.Name
                    });
                });
                yAxisValues.push({
                    name: bhrtcData.Name,
                    data: yValues,
                    pointPadding: 0,
                    color: bhrtcData.Name === bhRtcName ? '#917DFF' : '#B0DBDE',
                    showInLegend: true,
                });
            });

            xAxisValues = isData
                ? (bhRtcGraphSummary?.length > 1
                    ? bhRtcGraphSummary[1]?.map(x => x.Category)
                    : bhRtcGraphSummary[0]?.map(x => x.Category)) || []
                : [];

            setChartOptions(
                configureBhrtcChart(
                    'BH RTC/RF Costs',
                    '',
                    '',
                    'Cost',
                    yAxisValues,
                    xAxisValues,
                    'BhRtcCost',
                    history,
                    chartHeight
                ));
            setIsGraphShow(true);
            setIsPrepared(false);
        }
        else if (costSummaryTab === 4 && graphSummary && isPrepared) {
            let xAxisValues = graphSummary.map(x => x.Category);

            let yAxisValues = [
                {
                    name: 'Pharmaceutical Cost',
                    data: graphSummary.map((x) => ({
                        y: x.TotalCost,
                        total: x.MemberCount
                    })),
                    color: '#6495ED',
                    showInLegend: false
                }
            ];

            setChartOptions(
                configureStackedBarChartForCost(
                    'Pharmaceutical Cost',
                    '',
                    '',
                    'Cost',
                    yAxisValues,
                    xAxisValues,
                    'PhCost',
                    history,
                    chartHeight
                )
            );

            setIsGraphShow(true);
            setIsPrepared(false);
        }
    }, [graphSummary, isPrepared, bhRtcGraphSummary, costSummaryTab, history]);

    const handleAlertsTabChange = (event: React.ChangeEvent<{}>, newValue: number) => {
        setCostSummaryTab(newValue);
        changeFilter(newValue);
    };

    const handleDownload = async () => {
        try {
            setDownLoading(true);
            await exportDetail(); // your existing function
        } finally {
            setDownLoading(false);
        }
    };

    const exportDetail = async () => {
        const type = costSummaryTab === 1 ? ClaimAnalyticEnum.FfCost : costSummaryTab === 2 ? ClaimAnalyticEnum.AcCost : costSummaryTab === 3 ? ClaimAnalyticEnum.BhRtcCost : ClaimAnalyticEnum.PhCost;
        const data = await exportAnalyticMembers(
            type,
            costSummaryHealthPlanIds,
            costSummaryTeam,
            costSummaryYears,
            costSummaryMonths,
            showCostSummaryInActiveTeams
        );

        const typeName = costSummaryTab === 1 ? 'AcuteBHCosts' : costSummaryTab === 2 ? 'AcuteCareCosts' : costSummaryTab === 3 ? 'BHRTC/RFCosts' : 'PhCosts';
        let blob = b64toBlob(data.base64String, 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet');
        let url = window.URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        var date = new Date();
        var mm = date.getMonth() + 1; // getMonth() is zero-based
        var dd = date.getDate();
        var dateInfo = [(mm > 9 ? '' : '0') + mm, (dd > 9 ? '' : '0') + dd, date.getFullYear()].join('');
        link.setAttribute('download', `${typeName} - ${dateInfo}.xlsx`);
        link.click();
        link.remove();
    };

    const renderCostSummaryChart = () => (
        <>
            <Box
                display="flex"
                alignItems="center"
                gap={1.5}
                sx={{
                    position: 'absolute',
                    right: 0,
                    top: 0,
                    zIndex: 1,
                }}
            >
                <Typography
                    variant="body2"
                    sx={{
                        color: 'text.secondary',
                        whiteSpace: 'nowrap',
                    }}
                >
                    Last claim updated: {lastUpdatedOn || 'N/A'}
                </Typography>

                <Box
                    onClick={!downLoading ? handleDownload : undefined}
                    sx={{
                        width: 24,
                        height: 24,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        cursor: downLoading ? 'default' : 'pointer',
                    }}
                >
                    {downLoading ? (
                        <CircularProgress size={20} />
                    ) : (
                        <FileDownloadIcon
                            sx={{
                                color: 'black',
                                fontSize: 24,
                            }}
                            titleAccess="Export"
                        />
                    )}
                </Box>
            </Box>

            <HighchartsReact
                highcharts={Highcharts}
                options={chartOptions}
            />
        </>
    );

    return (
        <Grid size={{ xs: 12, lg: 6 }}>
            <Paper className="smartdashboard-widget" elevation={5} sx={{ height: 'calc(100vh - 140px)', maxHeight: '650px', display: 'flex', flexDirection: 'column', overflow: 'auto' }}>
                <Grid container spacing={2} paddingLeft={2} paddingRight={2}>
                    <Grid size={12} textAlign="center">
                        <Typography display={'inline-block'} variant="h6" marginRight={1} color={'primary'} ref={ref}>
                            Member Cost Comparison
                        </Typography>
                    </Grid>
                    <FilterOptions
                        healthPlanIds={costSummaryHealthPlanIds}
                        setHealthPlanIds={setCostSummaryHealthPlanIds}
                        years={costSummaryYears}
                        setYears={setCostSummaryYears}
                        months={costSummaryMonths}
                        setMonths={setCostSummaryMonths}
                        team={costSummaryTeam}
                        setTeam={setCostSummaryTeam}
                        showInActiveTeams={showCostSummaryInActiveTeams}
                        setShowInActiveTeams={setShowCostSummaryInActiveTeams}
                        onFetch={() => changeFilter()}
                    />
                    <Grid size={12} display={'flex'}>
                        <Tabs sx={{ display: 'inline-flex', marginRight: 'auto' }} value={costSummaryTab} onChange={handleAlertsTabChange}>
                            <Tab value={1} label="Acute BH Costs" />
                            <Tab value={2} label="Acute Care Costs" />
                            <Tab value={3} label="BH RTC/RF Costs" />
                            <Tab value={4} label="Pharmaceutical Costs" />
                        </Tabs>
                    </Grid>
                    <Grid size={12} marginTop={1} height="40vh" sx={{ position: 'relative' }}>
                        {!isGraphShow ? (
                            <Box className="loader-center">
                                <CircularProgress />
                            </Box>
                        ) : null}
                        {costSummaryTab === 1 ? (
                            renderCostSummaryChart()
                        ) : costSummaryTab === 2 ? (
                            renderCostSummaryChart()
                        ) : costSummaryTab === 3 ? (
                            renderCostSummaryChart()
                        ) : (
                            renderCostSummaryChart()
                        )}
                    </Grid>
                </Grid>
            </Paper>
        </Grid>
    );
};

export default MemberCostComparison;
